import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import { ASSESSMENT_MODULES } from '../../config/assessmentConstants';
import styles from '../../styles/assessmentFlow.module.css';

const ModuleSelection: React.FC = () => {
  const { state, dispatch } = useAssessment();
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const handleModuleToggle = (moduleId: string) => {
    setSelectedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleContinue = () => {
    if (selectedModules.length > 0) {
      dispatch({ type: 'SET_MODULES_SELECTED', payload: selectedModules });
    } else {
      // If no modules selected, go directly to results
      dispatch({ type: 'SET_CURRENT_STEP', payload: 'results' });
    }
  };

  const handleBack = () => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: 'levelA' });
  };

  const getLevelAMessage = () => {
    const yesCount = typeof state.levelA.yes_count === 'number' ? state.levelA.yes_count : 0;
    if (yesCount >= 5) {
      return `You answered "yes" to ${yesCount} of 7 questions. It sounds like there are things in your life that are hurting you. Below are some specific areas where people often struggle. Do any of these feel relevant to what you're experiencing?`;
    } else if (yesCount >= 2) {
      return `You answered "yes" to ${yesCount} of 7 questions. It sounds like you've noticed some concerning patterns. Below are some specific areas where people often struggle. Do any of these feel relevant to what you're going through?`;
    } else {
      return `You answered "yes" to ${yesCount} of 7 questions. It sounds like you don't have a problem—and that's a good thing!`;
    }
  };

  const shouldShowAreaSelection = () => {
    const yesCount = typeof state.levelA.yes_count === 'number' ? state.levelA.yes_count : 0;
    return yesCount >= 2;
  };

  return (
    <div className={styles.moduleSelection}>
      <div className={styles.moduleHeader}>
        <h2>Choose Areas to Explore</h2>
        <p>{getLevelAMessage()}</p>
      </div>

      {shouldShowAreaSelection() ? (
        <>
          <div className={styles.moduleGrid}>
            <div className={styles.moduleList}>
              {ASSESSMENT_MODULES.map(module => (
                <div key={module.id} className={styles.moduleItem}>
                  <label className={styles.moduleLabel} htmlFor={`module-${module.id}`}>
                    <input
                      type="checkbox"
                      checked={selectedModules.includes(module.id)}
                      onChange={() => handleModuleToggle(module.id)}
                      id={`module-${module.id}`}
                      className={styles.moduleCheckbox}
                    />
                    <span className={styles.moduleName}>{module.name}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.moduleActions}>
            <button
              onClick={handleBack}
              className={styles.backButton}
              type="button"
            >
              Back to Questions
            </button>
            
            <button
              onClick={handleContinue}
              className={styles.continueButton}
              type="button"
            >
              {selectedModules.length > 0 
                ? `Continue with ${selectedModules.length} area${selectedModules.length > 1 ? 's' : ''}`
                : 'Skip to Results'
              }
            </button>
          </div>

          {selectedModules.length > 0 && (
            <div className={styles.selectionSummary}>
              <h4>Selected areas:</h4>
              <div className={styles.selectedModules}>
                {selectedModules.map(moduleId => {
                  const module = ASSESSMENT_MODULES.find(m => m.id === moduleId);
                  return (
                    <span key={moduleId} className={styles.selectedModule}>
                      {module?.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.noSelectionNeeded}>
          <p>Since you haven't identified concerning patterns, you can skip the specific area questions and go straight to your results.</p>
          <div className={styles.moduleActions}>
            <button
              onClick={handleBack}
              className={styles.backButton}
              type="button"
            >
              Back to Questions
            </button>
            
            <button
              onClick={() => dispatch({ type: 'SET_CURRENT_STEP', payload: 'results' })}
              className={styles.continueButton}
              type="button"
            >
              See Results
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleSelection;
